import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  Divider,
  Stack,
  Typography
} from "@mui/material";
import { ExpandMoreOutlined } from "@mui/icons-material";
import React from "react";
import { tokens, mono } from "../../../theme";
import { absent } from "../../../utils/missing";
import EmptyState from "../emptyState/emptyState";

interface TableCardsProps {
  /** The same MRT table instance passed to <MaterialReactTable table={...} />. */
  table: any;
  /** Column ids to lead with, in order. Others follow in their table order. */
  primaryColumns?: string[];
}

// MRT's own display columns (select, actions, expand) carry no data.
const isDataColumn = (id: string) => !id.startsWith("mrt-");

const labelSx = {
  fontFamily: mono,
  fontSize: 10,
  letterSpacing: tokens.typography.eyebrowTracking,
  textTransform: "uppercase" as const,
  color: tokens.color.faint
};

/**
 * Mobile renderer for material-react-table, which ships none.
 *
 * Below `md` a 12-column table is unreadable, so each row becomes a Card of
 * label/value pairs built from the same instance — same rows, same filtering,
 * same sorting, because it reads the table's own row model rather than the raw
 * data. Values come from `cell.getValue()`; a nullish one renders the shared
 * `absent` glyph rather than an empty gap, so "blank" still reads as "we do not
 * have this" per the missing-data rules.
 */
const TableCards = ({ table, primaryColumns = [] }: TableCardsProps) => {
  const rows = table.getRowModel().rows ?? [];
  // A detail panel is real content — the grades table hides each student's
  // grade items in one. Without this it would simply vanish below md.
  const renderDetailPanel = table.options?.renderDetailPanel;

  if (rows.length === 0) {
    return (
      <EmptyState
        kind="empty"
        title="Nothing to show"
        body="This table has no rows for the current selection."
      />
    );
  }

  const order = (cells: any[]) => {
    const data = cells.filter((c) => isDataColumn(c.column.id));
    if (primaryColumns.length === 0) return data;
    const rank = (id: string) => {
      const i = primaryColumns.indexOf(id);
      return i === -1 ? primaryColumns.length : i;
    };
    return [...data].sort((a, b) => rank(a.column.id) - rank(b.column.id));
  };

  return (
    <Stack spacing={1.5}>
      {rows.map((row: any) => {
        const cells = order(row.getVisibleCells());
        const [lead, ...rest] = cells;
        return (
          <Card key={row.id} variant="outlined" sx={{ p: 1.75 }}>
            {lead && (
              <>
                <Typography sx={{ fontSize: tokens.typography.cardTitle, fontWeight: 600 }}>
                  {formatValue(lead.getValue())}
                </Typography>
                <Divider sx={{ my: 1.25 }} />
              </>
            )}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
                columnGap: 1.5,
                rowGap: 1.25
              }}
            >
              {rest.map((cell: any) => (
                <Box key={cell.id} sx={{ minWidth: 0 }}>
                  <Typography sx={labelSx}>{headerOf(cell)}</Typography>
                  <Typography sx={{ fontSize: tokens.typography.body, wordBreak: "break-word" }}>
                    {formatValue(cell.getValue())}
                  </Typography>
                </Box>
              ))}
            </Box>

            {renderDetailPanel && (
              <Accordion
                disableGutters
                elevation={0}
                sx={{ mt: 1.25, "&:before": { display: "none" }, bgcolor: "transparent" }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreOutlined />}
                  sx={{ minHeight: 48, px: 0 }}
                >
                  <Typography sx={{ fontSize: tokens.typography.body, color: tokens.color.primary }}>
                    Details
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 0, overflowX: "auto" }}>
                  {renderDetailPanel({ row, table })}
                </AccordionDetails>
              </Accordion>
            )}
          </Card>
        );
      })}
    </Stack>
  );
};

const headerOf = (cell: any) => {
  const header = cell.column.columnDef?.header;
  return typeof header === "string" ? header : cell.column.id;
};

const formatValue = (value: unknown): React.ReactNode => {
  if (value === null || value === undefined || value === "") return absent;
  if (typeof value === "object") return String(value);
  return String(value);
};

export default TableCards;
